'use client'

import {
  BeautyFrame,
  SafeText,
  beautyInk,
  beautyMuted,
  beautySans,
  beautySerif,
  clampIndex,
  getBeautyCopy,
  getBeautyDesign,
  getBeautyVisual,
  type BeautyPackProps,
} from '../shared/BeautyPackCore'

const bg = [
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/authority_01.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/authority_02.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/authority_03.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/authority_04.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/authority_05.jpg',
]

const total = 5

function splitHeadlineForAccent(text: string) {
  const clean = String(text || '').trim()
  const words = clean.split(/\s+/).filter(Boolean)

  if (words.length <= 2) {
    return {
      first: clean,
      accent: '',
    }
  }

  if (words.length <= 5) {
    return {
      first: words.slice(0, words.length - 1).join(' '),
      accent: words.slice(words.length - 1).join(' '),
    }
  }

  const accentCount = Math.min(3, Math.max(2, Math.floor(words.length * 0.28)))
  return {
    first: words.slice(0, words.length - accentCount).join(' '),
    accent: words.slice(words.length - accentCount).join(' '),
  }
}

function FullBgImage({
  src,
  objectPosition = 'center',
  blur = false,
}: {
  src?: string | null
  objectPosition?: string
  blur?: boolean
}) {
  if (!src) return null

  return (
    <img
      src={src}
      alt=""
      className="absolute inset-0 h-full w-full object-cover"
      style={{
        objectPosition,
        filter: blur ? 'blur(12px)' : 'none',
        transform: blur ? 'scale(1.06)' : 'scale(1)',
      }}
    />
  )
}

function TopDivider({
  accent,
  light = false,
}: {
  accent: string
  light?: boolean
}) {
  const lineColor = light ? 'rgba(255,255,255,0.34)' : `${accent}66`
  const capsuleBorder = light ? 'rgba(255,255,255,0.28)' : `${accent}99`
  const dotColor = light ? '#fff' : accent

  return (
    <div className="absolute left-[74px] right-[74px] top-[48px] z-20 flex items-center gap-4">
      <div className="h-px flex-1" style={{ background: lineColor }} />
      <div
        className="flex h-[56px] w-[56px] items-center justify-center rounded-[18px] border"
        style={{ borderColor: capsuleBorder }}
      >
        <div
          className="h-[8px] w-[8px] rounded-full"
          style={{ background: dotColor }}
        />
      </div>
      <div className="h-px flex-1" style={{ background: lineColor }} />
    </div>
  )
}

function CoverSlide({
  copy,
  accent,
  visual,
}: {
  copy: ReturnType<typeof getBeautyCopy>
  accent: string
  visual: ReturnType<typeof getBeautyVisual>
}) {
  const headline = splitHeadlineForAccent(
    copy.headline || 'La piel no necesita más ruido. Necesita criterio.'
  )

  return (
    <>
      <FullBgImage
        src={visual.src}
        blur={visual.blur}
        objectPosition="72% center"
      />

      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(246,238,227,0.82)_0%,rgba(246,238,227,0.68)_28%,rgba(246,238,227,0.12)_56%,rgba(246,238,227,0.04)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0)_24%,rgba(0,0,0,0.04)_100%)]" />

      <div className="absolute left-[36px] top-[62px] z-20">
        <SafeText
          className="text-[12px] font-bold uppercase tracking-[0.24em]"
          style={{ color: 'rgba(33,24,21,0.86)', fontFamily: beautySans }}
        >
          {copy.eyebrow || 'Beauty authority'}
        </SafeText>
      </div>

      <div className="absolute left-[36px] top-[168px] z-20 max-w-[248px]">
        <SafeText
          className="text-[64px] font-normal leading-[0.90] tracking-[-0.05em]"
          style={{ color: beautyInk, fontFamily: beautySerif }}
        >
          {headline.first}
          {headline.accent ? (
            <>
              <br />
              <span style={{ color: accent }}>{headline.accent}</span>
            </>
          ) : null}
        </SafeText>

        <div
          className="mt-[26px] h-px w-[72px]"
          style={{ background: `${accent}B3` }}
        />

        <SafeText
          className="mt-[24px] max-w-[250px] text-[18px] leading-[1.42]"
          style={{
            color: 'rgba(33,24,21,0.82)',
            fontFamily: beautySans,
          }}
        >
          {copy.subheadline ||
            'Una mirada editorial sobre hábitos, ingredientes y decisiones que sí aportan valor a tu piel.'}
        </SafeText>
      </div>
    </>
  )
}

function BodySlide({
  slideNumber,
  copy,
  accent,
  visual,
}: {
  slideNumber: string
  copy: ReturnType<typeof getBeautyCopy>
  accent: string
  visual: ReturnType<typeof getBeautyVisual>
}) {
  return (
    <>
      <FullBgImage
        src={visual.src}
        blur={visual.blur}
        objectPosition="76% center"
      />

      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(246,238,227,0.93)_0%,rgba(246,238,227,0.90)_34%,rgba(246,238,227,0.74)_52%,rgba(246,238,227,0.16)_74%,rgba(246,238,227,0.05)_100%)]" />

      <TopDivider accent={accent} />

      <div className="absolute left-[30px] top-[118px] z-20">
        <SafeText
          className="text-[44px] font-semibold tracking-[0.03em]"
          style={{ color: accent, fontFamily: beautySerif }}
        >
          {slideNumber}
        </SafeText>

        <SafeText
          className="mt-[14px] text-[12px] font-bold uppercase tracking-[0.22em]"
          style={{ color: beautyInk, fontFamily: beautySans }}
        >
          {copy.eyebrow || 'Punto de vista'}
        </SafeText>

        <div
          className="mt-[16px] h-px w-[114px]"
          style={{ background: `${accent}B3` }}
        />
      </div>

      <div className="absolute left-[30px] top-[232px] z-20 max-w-[268px]">
        <SafeText
          className="text-[58px] font-normal leading-[0.92] tracking-[-0.055em]"
          style={{ color: beautyInk, fontFamily: beautySerif }}
        >
          {copy.headline || 'La piel responde mejor a la constancia que al exceso.'}
        </SafeText>

        <div
          className="mt-[26px] h-px w-[48px]"
          style={{ background: `${accent}B3` }}
        />

        <SafeText
          className="mt-[26px] max-w-[252px] text-[18px] leading-[1.48]"
          style={{
            color: 'rgba(33,24,21,0.74)',
            fontFamily: beautySans,
          }}
        >
          {copy.body ||
            copy.subheadline ||
            'Una rutina bien pensada no busca impresionar: busca acompañar la salud y el equilibrio natural de tu piel.'}
        </SafeText>

        <SafeText
          className="mt-[22px] max-w-[248px] text-[13px] font-bold uppercase leading-[1.45] tracking-[0.16em]"
          style={{ color: accent, fontFamily: beautySans }}
        >
          {copy.proof || 'La constancia vence al exceso.'}
        </SafeText>
      </div>
    </>
  )
}

function ClosingSlide({
  copy,
  accent,
  visual,
}: {
  copy: ReturnType<typeof getBeautyCopy>
  accent: string
  visual: ReturnType<typeof getBeautyVisual>
}) {
  return (
    <>
      <FullBgImage
        src={visual.src}
        blur={visual.blur}
        objectPosition="center"
      />

      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.18)_0%,rgba(0,0,0,0.12)_18%,rgba(0,0,0,0.52)_72%,rgba(0,0,0,0.64)_100%)]" />

      <div className="absolute left-[36px] top-[62px] z-20">
        <SafeText
          className="text-[12px] font-bold uppercase tracking-[0.24em]"
          style={{ color: 'rgba(255,255,255,0.94)', fontFamily: beautySans }}
        >
          {copy.eyebrow || 'El resultado'}
        </SafeText>
      </div>

      <div className="absolute bottom-[82px] left-[36px] right-[36px] z-20 text-white">
        <SafeText
          className="max-w-[360px] text-[56px] font-normal leading-[0.92] tracking-[-0.05em]"
          style={{ fontFamily: beautySerif }}
        >
          {copy.headline || 'No es solo mejor piel. Es una mejor relación contigo.'}
        </SafeText>

        <SafeText
          className="mt-[20px] max-w-[310px] text-[18px] leading-[1.46]"
          style={{
            color: 'rgba(255,255,255,0.86)',
            fontFamily: beautySans,
          }}
        >
          {copy.result ||
            copy.subheadline ||
            copy.body ||
            'Cuando eliges una rutina con criterio, también eliges una forma más amable de cuidarte.'}
        </SafeText>

        {copy.cta ? (
          <div
            className="mt-[28px] inline-flex items-center rounded-[2px] border px-[28px] py-[14px] text-[13px] font-bold uppercase tracking-[0.18em]"
            style={{
              borderColor: 'rgba(255,255,255,0.48)',
              color: '#fff',
              fontFamily: beautySans,
            }}
          >
            {copy.cta}
          </div>
        ) : null}
      </div>
    </>
  )
}

export default function BeautyAuthorityPack({
  draft,
  activeIndex = 0,
  onActiveIndexChange,
  showControls = true,
  exportMode = false,
}: BeautyPackProps) {
  const index = clampIndex(activeIndex, total)
  const design = getBeautyDesign(draft, '#B18A4A')
  const accent = design.accent

  const copy = getBeautyCopy(draft, index, {
    eyebrow: 'Beauty authority',
    headline: 'La piel no necesita más ruido. Necesita criterio.',
    subheadline:
      'Una mirada editorial sobre hábitos, ingredientes y decisiones que sí aportan valor a tu piel.',
    body: 'La autoridad no se impone. Se construye con claridad, constancia y una postura coherente.',
    proof: 'La constancia vence al exceso.',
    result: 'Guardar este enfoque puede ser el primer paso hacia una rutina más consciente.',
    cta: 'Guardar este enfoque',
  })

  const visual = getBeautyVisual(draft, index, bg[index] || bg[0], {
    overlay: 'none',
    opacity: 0,
    blur: false,
  })

  return (
    <BeautyFrame
      activeIndex={index}
      total={total}
      onActiveIndexChange={onActiveIndexChange}
      showControls={showControls}
      nav={index === 4 ? 'light' : 'dark'}
      className="bg-[#F6EEE3]"
      borderRadius={exportMode ? 0 : 34}
    >
      {index === 0 ? (
        <CoverSlide copy={copy} accent={accent} visual={visual} />
      ) : null}

      {index === 1 ? (
        <BodySlide
          slideNumber="02"
          copy={copy}
          accent={accent}
          visual={visual}
        />
      ) : null}

      {index === 2 ? (
        <BodySlide
          slideNumber="03"
          copy={copy}
          accent={accent}
          visual={visual}
        />
      ) : null}

      {index === 3 ? (
        <BodySlide
          slideNumber="04"
          copy={copy}
          accent={accent}
          visual={visual}
        />
      ) : null}

      {index === 4 ? (
        <ClosingSlide copy={copy} accent={accent} visual={visual} />
      ) : null}
    </BeautyFrame>
  )
}